import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

export const ImageLightbox = ({ open, images = [], startIndex = 0, title, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const touchX = useRef(null);

  useEffect(() => { if (open) setIdx(startIndex); }, [open, startIndex]);

  const prev = useCallback(() => setIdx((i) => (i - 1 + images.length) % images.length), [images.length]);
  const next = useCallback(() => setIdx((i) => (i + 1) % images.length), [images.length]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, prev, next, onClose]);

  if (!open || images.length === 0) return null;

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50) (dx > 0 ? prev() : next());
    touchX.current = null;
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col animate-in fade-in duration-200"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      data-testid="image-lightbox"
    >
      <div className="flex items-center justify-between px-4 py-3 text-white/80">
        <div className="min-w-0">
          {title && <p className="text-sm truncate" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{title}</p>}
          <p className="text-xs tracking-widest uppercase" data-testid="lightbox-counter">
            Piece {idx + 1} / {images.length}
          </p>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
          data-testid="lightbox-close"
        >
          <X className="h-6 w-6" />
        </button>
      </div>

      <div className="flex-1 relative flex items-center justify-center px-2 pb-2 min-h-0">
        <img
          key={images[idx]}
          src={images[idx]}
          alt={`${title || 'Photo'} ${idx + 1}`}
          className="max-h-full max-w-full object-contain select-none animate-in fade-in duration-200"
          data-testid="lightbox-image"
        />
        {images.length > 1 && (
          <>
            <button
              onClick={prev}
              className="absolute left-2 md:left-6 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
              aria-label="Previous"
              data-testid="lightbox-prev"
            >
              <ChevronLeft className="h-6 w-6" />
            </button>
            <button
              onClick={next}
              className="absolute right-2 md:right-6 p-3 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
              aria-label="Next"
              data-testid="lightbox-next"
            >
              <ChevronRight className="h-6 w-6" />
            </button>
          </>
        )}
      </div>

      {images.length > 1 && (
        <div className="flex gap-2 overflow-x-auto px-4 py-3 border-t border-white/10">
          {images.map((u, i) => (
            <button
              key={u}
              onClick={() => setIdx(i)}
              className={`h-14 w-14 flex-shrink-0 rounded overflow-hidden border-2 transition-colors ${i === idx ? 'border-white' : 'border-transparent opacity-60 hover:opacity-100'}`}
              data-testid={`lightbox-thumb-${i}`}
            >
              <img src={`${u}${u.includes('?') ? '&' : '?'}w=160`} alt="" className="h-full w-full object-cover" loading="lazy" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ImageLightbox;
